import React from 'react';
import { Button } from './ui/button';
import { ThumbsUp, ThumbsDown, ChevronDown, X, Edit } from 'lucide-react';
import { RandomCompany } from '../types/randomCompanies';

interface CompanyCardHeaderProps {
  company: RandomCompany;
  isNonOperating: boolean;
  isVoted: boolean;
  isExpanded?: boolean;
  onVote: (vote: 'good' | 'bad') => void;
  onExpand?: () => void;
  onEdit?: () => void;
}

export const CompanyCardHeader: React.FC<CompanyCardHeaderProps> = ({
  company,
  isNonOperating,
  isVoted,
  isExpanded = false,
  onVote,
  onExpand,
  onEdit
}) => {
  // Check if company is in edit mode (voted but temporarily showing as neutral for re-voting)
  const isInEditMode = company.isInEditMode;
  const showVoteButtons = !company.voted || isInEditMode;

  return (
    <div>
      <div className={`flex items-center ${isVoted ? 'justify-between' : 'justify-between'}`}>
        <span className={`font-semibold ${isVoted && !isExpanded ? 'text-xs' : 'text-sm'} transition-all duration-500 ${
          isNonOperating ? 'text-slate-500' : 'text-white'
        }`}>
          {company.business_name || company.name}
          {isNonOperating && (
            <span className="ml-2 text-xs text-slate-600">
              {company.is_parked ? '(Parked)' : '(Not Operating)'}
            </span>
          )}
        </span>
        
        {showVoteButtons ? (
          <div className="flex gap-3 ml-4">
            <Button
              size="sm"
              onClick={() => onVote('good')}
              className={`h-10 w-10 p-0 bg-transparent hover:bg-green-50 text-green-500 border-2 border-green-500 shadow-lg hover:shadow-green-300/50 transition-all transform hover:scale-110 ${
                company.jigglingButton === 'thumbsUp' ? 'animate-pulse scale-110' : ''
              }`}
              style={{
                animation: company.jigglingButton === 'thumbsUp' 
                  ? 'jiggle 0.5s ease-in-out infinite alternate' 
                  : undefined
              }}
            >
              <ThumbsUp className="w-5 h-5" />
            </Button>
            <Button
              size="sm"
              onClick={() => onVote('bad')}
              className={`h-10 w-10 p-0 bg-transparent hover:bg-gray-100 text-white border-2 border-white shadow-lg hover:shadow-gray-300/50 transition-all transform hover:scale-110 ${
                company.jigglingButton === 'thumbsDown' ? 'animate-pulse scale-110' : ''
              }`}
              style={{
                animation: company.jigglingButton === 'thumbsDown' 
                  ? 'jiggle 0.5s ease-in-out infinite alternate' 
                  : undefined
              }}
            >
              <ThumbsDown className="w-5 h-5" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 ml-4">
            <div className={`flex items-center gap-1 text-xs ${
              company.voted === 'good' ? 'text-green-400' : 'text-red-400'
            }`}>
              {company.voted === 'good' ? (
                <ThumbsUp className="w-3 h-3" />
              ) : (
                <ThumbsDown className="w-3 h-3" />
              )}
              <span className="capitalize">{company.voted}</span>
            </div>
            {onEdit && isExpanded && (
              <Button
                size="sm"
                onClick={onEdit}
                variant="ghost"
                className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-600"
                title="Edit vote"
              >
                <Edit className="w-3 h-3" />
              </Button>
            )}
            {onExpand && (
              <Button
                size="sm"
                onClick={onExpand}
                variant="ghost"
                className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-600"
                title={isExpanded ? "Close" : "Expand"}
              >
                {isExpanded ? (
                  <X className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        )}
      </div>
      {company.domain && (!isVoted || isExpanded) && (
        <div className={`text-xs ${
          isNonOperating ? 'text-slate-600' : 'text-slate-300'
        }`}>
          {company.domain}
        </div>
      )}
    </div>
  );
}; 